import React, { useMemo, useState, useEffect } from 'react';
import Navbar from '../../../../../components/navbar.jsx';
import Footer from '../../../../../components/footer.jsx';
import Button from '../../../../../components/Button.jsx';
import Pagination from '../../../../../components/Pagination.jsx';
import DatePicker from '../../../../../components/DatePicker.jsx';
import { ReportsSidebar, ReportsTable } from '../../components/index.js';
import { getBranchByCode } from '../../../../../api/branchManagement]/branchApi.js';
import PrintReport from '../../components/PrintReport.jsx';
import styles from '../css/HourlyDistribution.module.css';

const HourlyDistribution = () => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [branchInfo, setBranchInfo] = useState(null);

  // Max date set to today to prevent future date selection
  const maxDate = new Date();

  // Fetch branch information when component mounts
  useEffect(() => {
    const fetchBranchInfo = async () => {
      try {
        // Get user info from localStorage
        const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
        if (userInfo.branchcode) {
          const branchData = await getBranchByCode(userInfo.branchcode);
          setBranchInfo(branchData);
        }
      } catch (error) {
        console.error('Error fetching branch info:', error);
      }
    };

    fetchBranchInfo();
  }, []);

  const rows = useMemo(
    () => [
      {
        hh: '05',
        gross: '526',
        discount: '0.00',
        admin: '0.00',
        nett: '1090.00',
        member: '67.00',
        claim: '914.00',
        rxItems: '1',
        rxItems2: '8',
        avg: '3.00',
        avg2: '545.30',
        otc: '44',
        nd: '38',
        mix: '0',
        rpt: '71',
        rw: '1',
      },
      {
        hh: '08',
        gross: '503',
        discount: '0.00',
        admin: '3.30',
        nett: '2378.27',
        member: '4000.00',
        claim: '963.00',
        rxItems: '2',
        rxItems2: '7',
        avg: '1.20',
        avg2: '237.83',
        otc: '56',
        nd: '54',
        mix: '0',
        rpt: '57',
        rw: '2',
      },
      {
        hh: '09',
        gross: '882',
        discount: '0.00',
        admin: '0.00',
        nett: '8855.48',
        member: '300.00',
        claim: '103.00',
        rxItems: '3',
        rxItems2: '9',
        avg: '461',
        avg2: '288.73',
        otc: '10',
        nd: '66',
        mix: '1',
        rpt: '11',
        rw: '3',
      },
      {
        hh: '13',
        gross: '529',
        discount: '0.00',
        admin: '0.00',
        nett: '12065.63',
        member: '560.00',
        claim: '443.00',
        rxItems: '4',
        rxItems2: '5',
        avg: '1.72',
        avg2: '613.96',
        otc: '45',
        nd: '75',
        mix: '0',
        rpt: '24',
        rw: '4',
      },
    ],
    []
  );

  const rowsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(rows.length / rowsPerPage));
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (safeCurrentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pageRows = rows.slice(startIndex, endIndex);

  const columns = useMemo(
    () => [
      { header: 'HH', accessor: 'hh' },
      { header: 'Gross', accessor: 'gross' },
      { header: 'Discount', accessor: 'discount' },
      { header: 'Admin', accessor: 'admin' },
      { header: 'Nett', accessor: 'nett' },
      { header: 'Member', accessor: 'member' },
      { header: 'Claim', accessor: 'claim' },
      { header: '#RX Items', accessor: 'rxItems' },
      { header: '', accessor: 'rxItems2' },
      { header: '(--Average--)', accessor: 'avg' },
      { header: ' ', accessor: 'avg2' },
      { header: 'OTC', accessor: 'otc' },
      { header: 'N/D', accessor: 'nd' },
      { header: 'Mix', accessor: 'mix' },
      { header: 'RPT', accessor: 'rpt' },
      { header: 'Rw', accessor: 'rw' },
    ],
    []
  );

  const handlePrint = () => {
    PrintReport({
      reportType: 'hourly',
      reportTitle: 'HOURLY DISTRIBUTION REPORT',
      branchInfo,
      headers: ['HH', 'Gross', 'Discount', 'Admin', 'Nett', 'Member', 'Claim', '#RX Items', 'RX Items2', 'Avg', 'Avg2', 'OTC', 'N/D', 'Mix', 'RPT', 'Rw'],
      data: rows, // Use all rows for print (not just current page)
      dateRange: { fromDate, toDate },
      isPrint: true
    });
  };

  const handleExport = () => {
    PrintReport({
      reportType: 'hourly',
      reportTitle: 'HOURLY DISTRIBUTION REPORT',
      branchInfo,
      headers: ['HH', 'Gross', 'Discount', 'Admin', 'Nett', 'Member', 'Claim', '#RX Items', 'RX Items2', 'Avg', 'Avg2', 'OTC', 'N/D', 'Mix', 'RPT', 'Rw'],
      data: rows, // Use all rows for export (not just current page)
      dateRange: { fromDate, toDate },
      isPrint: false
    });
  };

  return (
    <div className={styles.container}>
      <Navbar backTo="/customer/dispensary" backReplace />

      <div className={styles.mainContent}>
        <div className={styles.layout}>
          <div className={styles.sidebarContainer}>
            <ReportsSidebar />
          </div>

          <div className={styles.content}>
            <div className={styles.reportContainer}>
              <div className={styles.dateFiltersSection}>
                <div className={styles.dateFilters}>
                  <div className={styles.dateFilter}>
                    <label className={styles.dateLabel}>From</label>
                    <DatePicker
                      selectedDate={fromDate}
                      onDateChange={setFromDate}
                      placeholder=""
                      className={styles.dateInput}
                      maxDate={maxDate}
                      disableFutureDates={true}
                    />
                  </div>

                  <div className={styles.dateFilter}>
                    <label className={styles.dateLabel}>To</label>
                    <DatePicker
                      selectedDate={toDate}
                      onDateChange={setToDate}
                      placeholder=""
                      className={styles.dateInput}
                      maxDate={maxDate}
                      disableFutureDates={true}
                    />
                  </div>
                </div>
              </div>

              <div className={styles.actionButtonsSection}>
                <div className={styles.actionButtons}>
                  <Button onClick={handlePrint} variant="approve" className={styles.printButton}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9V2h12v7" />
                      <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 01-2 2h-2" />
                      <rect x="6" y="14" width="12" height="8" />
                    </svg>
                    Print
                  </Button>

                  <Button onClick={handleExport} variant="primary" className={styles.exportButton}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                      <polyline points="7,10 12,15 17,10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Export
                  </Button>
                </div>
              </div>

              <div className={styles.tableWrapper}>
                <ReportsTable columns={columns} data={pageRows} striped={true} />
              </div>

              <div className={styles.paginationContainer}>
                <Pagination
                  currentPage={safeCurrentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HourlyDistribution;
